import React from 'react';
import { Link } from 'react-router-dom';
import {
  calculateAlbertaLandTitleTransferFee,
  albertaClosingCostEstimates,
} from '../../../utils/albertaLandTitleTransferFee';
import { calculateOntarioLandTransferTax } from '../../../utils/ontarioLandTransferTax';
import { formatUsd } from '../../../utils/articleScenarioMetrics';

const CALGARY_PRICE = 700_000;
const EDMONTON_PRICE = 550_000;
const calgaryFee = calculateAlbertaLandTitleTransferFee(CALGARY_PRICE);
const edmontonFee = calculateAlbertaLandTitleTransferFee(EDMONTON_PRICE);
const ontarioLttCalgary = calculateOntarioLandTransferTax(CALGARY_PRICE);
const calgaryEst = albertaClosingCostEstimates({
  purchasePrice: CALGARY_PRICE,
  cmhcPremium: Math.round(CALGARY_PRICE * 0.9 * 0.031),
});
const calgaryBaseLoan = CALGARY_PRICE * 0.9;
const calgaryCmhcPremium = Math.round(calgaryBaseLoan * 0.031);

export const AlbertaCalculatorContext: React.FC = () => (
  <div className="prose prose-stone max-w-none text-ink-muted space-y-4">
    <h3 className="font-serif text-xl text-ink">No land transfer tax: what Alberta buyers save at closing</h3>
    <p>
      Alberta does not charge provincial land transfer tax on home purchases. Buyers pay a land title
      registration fee instead: $50 plus $5.00 per $5,000 of property value. On the Calgary tab at{' '}
      {formatUsd(CALGARY_PRICE)}, that fee is {formatUsd(calgaryFee)}. Edmonton at {formatUsd(EDMONTON_PRICE)}{' '}
      runs {formatUsd(edmontonFee)}. On the same {formatUsd(CALGARY_PRICE)} purchase in Ontario, provincial
      land transfer tax alone is {formatUsd(ontarioLttCalgary)} before rebates, and Toronto buyers would pay
      that amount twice. The closing cost panel above shows you save {formatUsd(calgaryEst.lttSavingsVsOntario)}{' '}
      versus Ontario provincial LTT, or {formatUsd(calgaryEst.lttSavingsVsToronto)} versus a Toronto dual-tax
      purchase at the same price.
    </p>
    <p>
      Both pre-loaded tabs use 10% down with CMHC insurance at 5.20% fixed over 25 years. Calgary property
      taxes are modeled at 0.65% of value; Edmonton at 0.70%. Because Alberta has no provincial sales tax,
      there is zero PST on the CMHC premium at closing. On Calgary, a {formatUsd(calgaryCmhcPremium)} premium
      rolls into the mortgage with no additional cash charge, unlike Ontario (8% PST) or BC (7% PST). Legal
      fees, title insurance, and home inspection still apply in Alberta as separate closing-day costs.
    </p>
    <p>
      The mortgage stress test applies at federally regulated lenders province-wide, so qualifying rate rules
      are identical to Ontario and BC. What changes is the cash stack at the lawyer&apos;s office. Compare the
      Calgary and Edmonton tabs to see how a higher Calgary purchase price affects insured monthly payments
      even when closing fees stay low. Adjust the down payment or rate in the calculator to match your own
      pre-approval numbers.
    </p>
    <p className="text-xs text-ink-muted border-l-4 border-stone-200 pl-3">
      Sources: Government of Alberta, &quot;Register a land title document or plan,&quot; accessed June 2026. URL:{' '}
      <a
        href="https://www.alberta.ca/register-land-title-document-plan"
        className="text-gold-700 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.alberta.ca/register-land-title-document-plan
      </a>
      . See also our{' '}
      <Link to="/learn/guides/mortgage-calculator-alberta-guide" className="text-gold-700 hover:underline">
        Alberta mortgage guide
      </Link>
      .
    </p>
  </div>
);
